import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LabelComponent, RootDirective, SvgDirective } from '@pure/hive';
import { WorkflowRecipeSignal } from '../../../../data/models/incident';
import { WorkflowRecipeRenderInfo } from '../../../../data/models/recommendations.models';
import { registerAsCardPlugin } from '../../services/recommendation-component-registry.service';

/** Stable registry key for this plugin. Used as the lookup name on the corresponding RenderInfo. */
export const WORKFLOW_RECIPE_CARD_PLUGIN_NAME = 'WorkflowRecipeCardPluginComponent';

/**
 * Glyph per signal, mirroring the mock, which draws a different one for each kind of
 * workflow card. Keyed the same way the card title is (see `WORKFLOW_RECIPE_CARD_TITLES`)
 * so a new signal cannot pick up a title without an icon to match — the icon lives here
 * rather than beside the title because a glyph is presentation.
 *
 * These are the mock's own vectors, exported from its "Everpure Standard Icons" library
 * into `assets/icons/everpure/` and loaded through Hive's `hive-svg`. Resolved against
 * `SVG_ROOT` ('images/'), which is where angular.json copies that directory. The export's
 * baked-in accent hex was swapped for `currentColor` so the SCSS drives it from a Hive
 * token and it follows the theme.
 *
 * A new signal needs its own vector exported here — do not substitute a lookalike from
 * another icon set.
 */
const SIGNAL_ICONS: Readonly<Record<string, string>> = {
    [WorkflowRecipeSignal.EvergreenOne]: 'everpure/recommended-recipe.svg',
};

/** Glyph for a signal this build does not map yet. */
const DEFAULT_SIGNAL_ICON = 'everpure/recommended-recipe.svg';

/**
 * How many recipes the card body lists before collapsing the rest into a "+N more"
 * marker; the dialog shows every recipe.
 *
 * One, because the grid row grows to its tallest card and every other recommendation
 * card needs 400px. Measured on the real page: three recipes took this card to 641px
 * and two to 482px, both dragging their row-mates up with them; one lands at 366px of
 * content, inside the row. It also matches the mock, which draws a single recipe.
 */
const MAX_VISIBLE_RECIPES = 1;

/**
 * Plugin component for the workflow-recipe (`prorec-eone-workflow-recipe`) card body.
 *
 * The shell (title, dismiss, click-to-open-dialog) comes from
 * `RecommendationCardTemplateComponent`; this plugin owns only the recipe list.
 * One card covers one signal and lists N recipes, so the body is a list, not a
 * single recipe's detail.
 */
@Component({
    selector: 'workflow-recipe-card-plugin',
    templateUrl: './workflow-recipe-card-plugin.component.html',
    styleUrls: ['./workflow-recipe-card-plugin.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [LabelComponent, RootDirective, SvgDirective],
})
export class WorkflowRecipeCardPluginComponent {
    renderInfo = input.required<WorkflowRecipeRenderInfo>();

    protected readonly signalIcon = computed(() => SIGNAL_ICONS[this.renderInfo().signal] ?? DEFAULT_SIGNAL_ICON);

    /** The recipes the card actually lists. */
    protected readonly visibleRecipes = computed(() => this.renderInfo().recipes.slice(0, MAX_VISIBLE_RECIPES));

    /** How many recipes are collapsed behind the "+N more" marker; 0 when all fit. */
    protected readonly hiddenRecipeCount = computed(() =>
        Math.max(0, this.renderInfo().recipes.length - MAX_VISIBLE_RECIPES),
    );
}

registerAsCardPlugin(WORKFLOW_RECIPE_CARD_PLUGIN_NAME, WorkflowRecipeCardPluginComponent);
